import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import LocalizedText from '@LocalizedText';
import useIntlMessage from '@app/hooks/use-intl-message';
import { setFooterConfig } from '@app/actions/footerActions';
import { processMultipleArtworkUpload } from '@app/actions/uploadActions';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isMobileMode, getFileName, getMultiUploadLimit } from '@app/utils/utils';
import SvgIconUse from '@SvgIconUse';
import StatusBanner, { STATUS_TYPES } from '@components/status-banner/StatusBanner';

import UploadArtwork from './upload-artwork/UploadArtwork';
import './MultiUploadView.scss';

const STATUS_ICON = {
  loading: 'svg-loading',
  success: 'svg-icon-check',
  error: 'svg-exclamation',
};

const MultiUploadView = ({
  matrix: { design: { product: productId, design: predesignId, quoteAssetId } = {}, ctx = {} },
  multiUploadLimit,
  setFooterConfig,
  processMultipleArtworkUpload,
}) => {
  const intl = useIntl();
  const [selectedFilesData, setSelectedFilesData] = useState([]);
  const [filesLimitExceeded, setFilesLimitExceeded] = useState(false);
  const [uploadsComplete, setUploadsComplete] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [finishedArtworksData, setFinishedArtworksData] = useState([]);
  const fileRequestsRef = useRef(new Map());
  const params = useMemo(
    () => ({
      productId,
      predesignId,
      quoteAssetId,
    }),
    [productId, predesignId, quoteAssetId],
  );
  const [uploadingAriaLabel, successAriaLabel, failedAriaLabel, pendingAriaLabel] = useIntlMessage(
    'qau-uploading-status-aria-label',
    'qau-upload-successful-status-aria-label',
    'qau-upload-failed-status-aria-label',
    'qau-upload-pending-status-aria-label',
  );
  const STATUS_ARIA_LABEL = {
    loading: uploadingAriaLabel,
    success: successAriaLabel,
    error: failedAriaLabel,
    '': pendingAriaLabel,
  };

  const uploadedCount = fileList.filter(f => f.status === 'success').length;
  const errorCount = fileList.filter(f => f.status === 'error').length;

  const getUploadStatus = () => {
    if (fileList.length === 0) return null;

    if (!uploadsComplete) {
      const uploadingCount = uploadedCount + errorCount + 1;
      return {
        type: STATUS_TYPES.INFO,
        message: (
          <FormattedMessage id="qau-multi-upload-status-uploading" values={{ uploaded: uploadingCount, total: fileList.length }} />
        ),
      };
    }
    if (errorCount > 0) {
      return {
        type: STATUS_TYPES.ERROR,
        message: <FormattedMessage id="qau-multi-upload-status-error-validation" />,
      };
    }
    if (filesLimitExceeded) {
      return {
        type: STATUS_TYPES.ERROR,
        message: <FormattedMessage id="qau-multi-upload-status-error-limit" values={{ limit: multiUploadLimit }} />,
      };
    }
    return {
      type: STATUS_TYPES.SUCCESS,
      message: <FormattedMessage id="qau-multi-upload-status-success" values={{ count: uploadedCount }} />,
    };
  };

  const uploadStatus = getUploadStatus();

  const getErrorMessage = ({ errorCode, code = errorCode, errorMessage, message = errorMessage }) => {
    return <LocalizedText id={`qau-multi-upload-error-${code}`} defaultMessage={message} />;
  };

  const getFilesToUpload = files => {
    const availableSlots = multiUploadLimit - fileList.length;

    if (files.length > availableSlots) {
      setFilesLimitExceeded(true);
    }

    return files.slice(0, availableSlots);
  };

  const updateFileStatus = (fileId, status, errors) => {
    if (fileRequestsRef.current.get(fileId) === null) {
      return false;
    }

    setFileList(prev => {
      return prev.map(file => (file.id === fileId ? { ...file, status, ...(errors && { error: errors[0] }) } : file));
    });
  };

  const handleFileSelect = files => {
    const filesToUpload = getFilesToUpload(files);
    const filesData = filesToUpload.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      filename: getFileName(file),
    }));
    setFileList(prev => [...filesData.map(({ id, file: { name } }) => ({ id, name })), ...prev]);

    setSelectedFilesData(filesData);
  };

  const resetSelectedFile = () => {
    setSelectedFilesData([]);
  };

  const processFinishedArtworks = useCallback(() => {
    const artworkInfos = finishedArtworksData.map(item => item.artworkData);
    const count = artworkInfos.length;

    window.top.postMessage(
      {
        type: 'multi-upload',
        data: artworkInfos,
        count: count,
      },
      '*',
    );

    window.close();
  }, [finishedArtworksData]);

  const handleDeleteFile = fileId => {
    const file = fileList.find(f => f.id === fileId);
    if (!file) return;

    const request = fileRequestsRef.current.get(fileId);
    if (request) {
      request.xhr.abort();
    }
    fileRequestsRef.current.set(fileId, null);

    setFileList(prev => prev.filter(f => f.id !== fileId));

    if (file.status === 'success') {
      setFinishedArtworksData(prev => prev.filter(artwork => artwork.fileId !== fileId));
    }

    setFilesLimitExceeded(fileList.length - 1 > multiUploadLimit);
  };

  useEffect(() => {
    if (!selectedFilesData.length) return;

    processMultipleArtworkUpload(selectedFilesData, params, updateFileStatus, fileRequestsRef.current).then(results => {
      const successfulArtworks = results
        .map((result, index) => {
          const fileData = selectedFilesData[index];
          if (result && result.data && !(result instanceof Error)) {
            return {
              fileId: fileData.id,
              artworkData: result.data,
            };
          }
          return null;
        })
        .filter(Boolean);

      setFinishedArtworksData(prev => [...prev, ...successfulArtworks]);
    });
  }, [selectedFilesData, processMultipleArtworkUpload, params]);

  useEffect(() => {
    setFooterConfig({
      onNext: processFinishedArtworks,
      backHidden: true,
      nextHidden: false,
      nextLocKey: !uploadsComplete ? 'qau-uploading-btn' : 'qau-finish-btn',
      nextDisabled: !uploadsComplete || uploadedCount === 0,
      visible: !!fileList.length,
      nextLoading: !uploadsComplete,
    });
  }, [setFooterConfig, uploadsComplete, fileList, processFinishedArtworks, uploadedCount]);

  useEffect(() => {
    setUploadsComplete(fileList.every(f => f.status && f.status !== 'loading'));
  }, [fileList]);

  return (
    <div id="qau-multi-upload-view">
      <div className="upload-title">
        <LocalizedText id="qau-title-upload-multiple-designs" />
      </div>
      <UploadArtwork
        handleFileSelect={handleFileSelect}
        resetSelectedFile={resetSelectedFile}
        isMobileView={isMobileMode(ctx)}
        multiple
        disabled={fileList.length >= multiUploadLimit}
        multiUploadLimit={multiUploadLimit}
      />
      {fileList.length > 0 && (
        <>
          <StatusBanner status={uploadStatus?.type} className="multi-upload-banner">
            {uploadStatus?.message}
          </StatusBanner>
          <div className="file-upload-list">
            {fileList.map(file => (
              <div
                key={file.id}
                className="file-upload-row"
                aria-label={`${file.name} - ${STATUS_ARIA_LABEL[file.status]}${
                  file.error ? ` - ${file.error.message || file.error.errorMessage}` : ''
                }`}
              >
                {file.status && (
                  <div className={`file-status status-${file.status}`}>
                    <span className={`status-icon ${STATUS_ICON[file.status]}`} />
                  </div>
                )}
                <div className={classnames('file-info', { error: file.error })}>
                  <div className="file-name" title={file.name}>
                    {file.name}
                  </div>
                  {file.error && <div className="file-error">{getErrorMessage(file.error)}</div>}
                </div>
                <button
                  type="button"
                  className="file-delete-btn"
                  onClick={() => handleDeleteFile(file.id)}
                  aria-label={intl.formatMessage({ id: 'qau-remove-file-aria-label' }, { fileName: file.name })}
                >
                  <SvgIconUse id="icon-x-circle" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ matrix }, { flowConfig = {} }) => ({
  matrix,
  multiUploadLimit: getMultiUploadLimit(flowConfig),
});

const mapDispatchToProps = {
  setFooterConfig,
  processMultipleArtworkUpload,
};

MultiUploadView.propTypes = {
  matrix: PropTypes.object.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  multiUploadLimit: PropTypes.number.isRequired,
  processMultipleArtworkUpload: PropTypes.func.isRequired,
};

export default inject('flowConfig')(connect(mapStateToProps, mapDispatchToProps)(MultiUploadView));
